import { useEffect, useState } from 'preact/hooks';
import { PlaceholderScreen } from './PlaceholderScreen';
import { getStats, ApiError, type ActorStats } from '../lib/api';
import { routePath, type AuditionStage } from '../routes/route';
import { tapProps } from '../lib/tap';
import './Career.css';

export interface CareerStatsProps {
  tenantId: string;
  /** R4 (Actionable Everywhere): drill a stat down to the auditions it's
   *  computed from. Optional so existing tests mount without it (the cells
   *  then render inert rather than as doorways). */
  onNavigate?: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const MEDIUM_LABEL: Record<string, string> = {
  film: 'Film',
  tv: 'Television',
  theatre: 'Theatre',
  new_media: 'New media',
  commercial: 'Commercial',
  voice: 'Voice / animation',
  other: 'Other',
  unknown: 'Unspecified',
};

// A fraction in [0,1] -> an honest whole-percent string.
function pct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

// CAREER / STATS — computed off REAL auditions. Callback % / Booking % / Time-
// to-CB dash (the last renders honestly when its history isn't available yet),
// By office, By type, and the deferred metrics shown calmly, never fabricated.
export function CareerStats({ tenantId, onNavigate }: CareerStatsProps) {
  // A doorway to the Auditions list at a given stage — the records the stat
  // above it is computed from. undefined when App didn't wire onNavigate, so
  // tapProps() returns an empty bag and the cell stays a plain read-out.
  const toStage = (stage: AuditionStage) =>
    onNavigate ? () => onNavigate(routePath({ screen: 'auditions', stage })) : undefined;
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<ActorStats | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getStats(tenantId);
        if (cancelled) return;
        setData(d);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (state === 'loading') {
    return <PlaceholderScreen eyebrow="Stats" line="One moment…" />;
  }
  if (state === 'error' || !data) {
    return <PlaceholderScreen eyebrow="Stats" line="Couldn't load your stats." soft={errorMessage ?? undefined} />;
  }

  // A fresh actor with no auditions yet — an inviting empty, not a wall of 0%.
  if (data.total === 0) {
    return (
      <PlaceholderScreen
        eyebrow="Stats"
        line="Not enough history yet."
        soft="Callback and booking rates build as your auditions come through."
      />
    );
  }

  const timeToCb = data.timeToCallbackDays;

  return (
    <div class="career">
      <div class="career__eyebrow">Stats · {data.total} auditions</div>

      <div class="dash">
        {/* Callback % is computed from the auditions that reached a callback —
            tap through to that stage. Booking % -> booked. Time-to-CB shares
            the callbacks its measurement is drawn from. */}
        <div class="dash__cell" {...tapProps(toStage('callback'))}>
          <div class="dash__lab">Callback %</div>
          <div class="dash__n">{pct(data.callbackRate)}</div>
        </div>
        <div class="dash__cell" {...tapProps(toStage('booked'))}>
          <div class="dash__lab">Booking %</div>
          <div class="dash__n">{pct(data.bookingRate)}</div>
        </div>
        <div
          class={`dash__cell${timeToCb === null ? ' dash__cell--empty' : ''}`}
          data-testid="time-to-cb"
          {...tapProps(toStage('callback'))}
        >
          <div class="dash__lab">Time to CB</div>
          <div class="dash__n">{timeToCb === null ? '—' : `${Math.round(timeToCb)}d`}</div>
          {timeToCb === null && <div class="dash__sub">not enough history yet</div>}
        </div>
      </div>

      {data.byOffice.length > 0 && (
        <>
          <div class="career__grp">By office — who keeps calling</div>
          {data.byOffice.map((o) => (
            // Section-level: no per-office filter in the route yet, so this
            // drills to the full Auditions list rather than that one office's.
            <div class="career__card" key={o.office} data-testid="office-card" {...tapProps(toStage('all'))}>
              <div class="career__card-top">
                <span class="career__card-nm">{o.office}</span>
              </div>
              <div class="career__card-sub">
                {o.count} {o.count === 1 ? 'audition' : 'auditions'} · {o.booked} booked · {pct(o.bookingRate)} book rate
              </div>
            </div>
          ))}
        </>
      )}

      {data.byType.length > 0 && (
        <>
          <div class="career__grp">By type</div>
          {data.byType.map((t) => (
            // Section-level for the same reason as by-office (no per-medium
            // route filter) — the full list, never inert.
            <div class="career__kv" key={t.medium} data-testid="type-row" {...tapProps(toStage('all'))}>
              <span class="career__kv-k">{MEDIUM_LABEL[t.medium] ?? t.medium}</span>
              <span class="career__kv-v">
                {pct(t.bookingRate)} book · {t.count}
              </span>
            </div>
          ))}
        </>
      )}

      <div class="career__grp">Headshot attribution</div>
      {data.headshotAttribution ? (
        <div class="career__kv" data-testid="headshot-attr">
          <span class="career__kv-k">{data.headshotAttribution.label ?? 'Top look'}</span>
          <span class="career__kv-v">{data.headshotAttribution.note ?? 'drives most callbacks'}</span>
        </div>
      ) : (
        <div class="career__note" data-testid="headshot-empty">
          Not enough data yet — this needs a headshot-to-audition link we don&rsquo;t track today.
        </div>
      )}

      {data.deferred.length > 0 && (
        <div class="career__deferred" data-testid="deferred">
          <div class="career__grp" style="margin-top:0">Still building</div>
          {data.deferred.map((reason) => (
            <div class="career__note" key={reason}>
              {reason.split(':')[0]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
